import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AdminUser } from '../models/auth.model';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  readonly currentAdmin = signal<AdminUser | null>(null);
  readonly isAuthenticated = computed(() => this.currentAdmin() !== null);
  readonly hasComplex = computed(() => !!this.currentAdmin()?.complexId);

  async login(email: string, password: string): Promise<void> {
    const tokens = await firstValueFrom(this.authService.login({ email, password }));
    this.authService.saveTokens(tokens);
    this.currentAdmin.set(this.decodeUser(tokens.accessToken));
  }

  logout(): void {
    this.authService.clearTokens();
    this.currentAdmin.set(null);
    this.router.navigate(['/login']);
  }

  setCurrentAdmin(accessToken: string): void {
    this.currentAdmin.set(this.decodeUser(accessToken));
  }

  async tryRestoreSession(): Promise<void> {
    const token = this.authService.getAccessToken();
    if (token && !this.authService.isTokenExpired()) {
      this.currentAdmin.set(this.decodeUser(token));
      return;
    }

    const refreshToken = this.authService.getRefreshToken();
    if (refreshToken) {
      try {
        const tokens = await firstValueFrom(this.authService.refresh(refreshToken));
        this.authService.saveTokens(tokens);
        this.currentAdmin.set(this.decodeUser(tokens.accessToken));
        return;
      } catch {
        this.authService.clearTokens();
      }
    }

    this.currentAdmin.set(null);
  }

  private decodeUser(token: string): AdminUser {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      id: payload.sub ?? payload.nameid ?? '',
      email: payload.email ?? '',
      fullName: payload.name ?? payload.unique_name ?? '',
      role: payload.role ?? '',
      complexId: payload.complex_id || null
    };
  }
}
